import React, { ComponentPropsWithoutRef, ComponentType, ReactElement, Suspense } from 'react'
import { Redirect } from 'react-router-dom'

import { Flag } from '@app/Feature/Components/Flag'
import { FeatureConfig } from '@app/Feature/models'

const isElement = (element): element is React.ReactElement | JSX.Element =>
  React.isValidElement(element)

const isString = (element): element is string => typeof element === 'string'

const Loading = null

const renderElements = (Elements: FeatureConfig['fallback']): ReactElement | null => {
  if (!Elements) {
    return null
  }

  if (isElement(Elements)) {
    return <React.Fragment>{Elements}</React.Fragment>
  }

  if (isString(Elements)) {
    return <span>{Elements}</span>
  }

  return <Elements />
}

const renderElementType = <P extends ComponentPropsWithoutRef<any>>(
  Component: ComponentType<P>,
  props: P
): ReactElement | null => {
  if (isElement(Component)) {
    return Component
  }

  return <Component {...(props as P)} />
}

export function createFeature<P>(config: FeatureConfig) {
  return (
    FeatureComponent: ComponentType<P>,
    FallbackComponent?: ComponentType<P>
  ): ComponentType<P> => {
    if (!FeatureComponent) {
      return () => null
    }

    const FlagComponent: React.FunctionComponent<P> = (props) => {
      const renderFeature = (): ReactElement | null => {
        return renderElementType(FeatureComponent, props)
      }

      const renderFallback = (): ReactElement | null => {
        if (FallbackComponent) {
          return renderElementType(FallbackComponent, props)
        }

        if (config.fallback) {
          return renderElements(config.fallback)
        }

        return <Redirect to={'/'} />
      }

      return (
        <Suspense fallback={Loading}>
          <Flag
            name={config.name}
            render={({ isEnabled }) => (isEnabled ? renderFeature() : renderFallback())}
          ></Flag>
        </Suspense>
      )
    }

    FlagComponent.displayName = `Feature(${config.name})`

    return FlagComponent
  }
}

export { FeatureGate } from './Components/FeatureGate'
export { Features } from './models'
